import React, { useEffect, useRef, useState } from "react";
import { ShoppingBag, BellRing, XCircle, X, BellOff, Bell, Volume2, VolumeX } from "lucide-react";

const TOAST_CONFIG = {
    new_order: {
        icon: ShoppingBag,
        label: "New Order Placed",
        color: "#6366f1",
        border: "#e0e0ff",
        bg: "#f5f3ff",
    },
    ready_order: {
        icon: BellRing,
        label: "Order Ready for Pickup",
        color: "#10b981",
        border: "#d1fae5",
        bg: "#f0fdf4",
    },
    cancelled: {
        icon: XCircle,
        label: "Order Cancelled",
        color: "#e65d5d",
        border: "#ffe0e0",
        bg: "#fff5f5",
    },
    cancelled_by_store: {
        icon: XCircle,
        label: "Order Cancelled By Store",
        color: "#e65d5d",
        border: "#ffe0e0",
        bg: "#fff5f5",
    },
};

const ToastItem = ({ order, onDismiss, onNavigate }) => {
    const cfg  = TOAST_CONFIG[order.status] ?? TOAST_CONFIG.new_order;
    const Icon = cfg.icon;

    return (
        <div
            onClick={() => { onNavigate(order.id); onDismiss(order.id); }}
            style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "11px 12px", borderRadius: 10,
                background: cfg.bg, border: `1px solid ${cfg.border}`,
                cursor: "pointer", transition: "transform 0.15s, box-shadow 0.15s",
                position: "relative",
            }}
            onMouseEnter={e => {
                e.currentTarget.style.transform = "translateX(-3px)";
                e.currentTarget.style.boxShadow = "2px 2px 8px rgba(0,0,0,0.08)";
            }}
            onMouseLeave={e => {
                e.currentTarget.style.transform = "translateX(0)";
                e.currentTarget.style.boxShadow = "none";
            }}
        >
            {/* Left accent bar */}
            <div style={{
                position: "absolute", left: 0, top: 0, bottom: 0, width: 3,
                borderRadius: "10px 0 0 10px", background: cfg.color,
            }} />

            {/* Icon bubble */}
            <div style={{
                width: 36, height: 36, borderRadius: 9, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: `${cfg.color}18`, marginLeft: 6,
            }}>
                <Icon size={18} color={cfg.color} />
            </div>

            {/* Text */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "0.84rem", fontWeight: 700, color: "#1a1a1a", lineHeight: 1.2 }}>
                    {cfg.label}
                </div>
                <div style={{ fontSize: "0.7rem", fontWeight: 600, color: "#71717a", fontFamily: "monospace", marginTop: 3 }}>
                    #{String(order.id).slice(-8)}
                </div>
            </div>

            {/* Dismiss X */}
            <button
                onClick={e => { e.stopPropagation(); onDismiss(order.id); }}
                title="Dismiss"
                style={{
                    background: "rgba(0,0,0,0.05)", border: "none", cursor: "pointer",
                    padding: 5, display: "flex", alignItems: "center", justifyContent: "center",
                    borderRadius: 6, flexShrink: 0, transition: "background 0.2s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(0,0,0,0.12)"}
                onMouseLeave={e => e.currentTarget.style.background = "rgba(0,0,0,0.05)"}
            >
                <X size={13} color="#475569" />
            </button>
        </div>
    );
};

export default function NotificationToaster({
                                                updatedOrders = [],
                                                onDismissOrder,
                                                muted,
                                                onToggleMute,
                                            }) {
    const [open, setOpen] = useState(false);
    const panelRef        = useRef(null);
    const bellRef         = useRef(null);
    const count           = updatedOrders.length;

    // Close when clicking outside
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (
                panelRef.current && !panelRef.current.contains(e.target) &&
                bellRef.current  && !bellRef.current.contains(e.target)
            ) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    return (
        <>
            <style>{`
                @keyframes bell-shake {
                    0%, 100% { transform: rotate(0deg); }
                    10% { transform: rotate(-18deg); }
                    20% { transform: rotate(18deg); }
                    30% { transform: rotate(-12deg); }
                    40% { transform: rotate(12deg); }
                    55% { transform: rotate(-5deg); }
                    65% { transform: rotate(5deg); }
                    75% { transform: rotate(0deg); }
                }
                @keyframes badge-pop {
                    0%   { transform: scale(0.4); opacity: 0; }
                    65%  { transform: scale(1.25); }
                    100% { transform: scale(1); opacity: 1; }
                }
                @keyframes glow-pulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(251,191,36,0.45); }
                    50%      { box-shadow: 0 0 0 7px rgba(251,191,36,0); }
                }
                @keyframes panel-slide {
                    from { opacity: 0; transform: translateY(-10px) scale(0.96); }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                .notif-bell-btn { transition: transform 0.18s ease, box-shadow 0.18s ease !important; }
                .notif-bell-btn:hover { transform: scale(1.08) !important; }
                .notif-bell-btn:active { transform: scale(0.95) !important; }
            `}</style>

            <div style={{ position: "relative", display: "inline-flex" }}>

                {/* ── Bell button ── */}
                <button
                    ref={bellRef}
                    className="notif-bell-btn"
                    onClick={() => setOpen(o => !o)}
                    title="Notifications"
                    style={{
                        width: 46, height: 46, borderRadius: 13,
                        background: count > 0
                            ? "linear-gradient(145deg, #fef3c7 0%, #fde68a 100%)"
                            : "#f1f5f9",
                        border: `1.5px solid ${count > 0 ? "#fbbf24" : "#e2e8f0"}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        cursor: "pointer", outline: "none", padding: 0,
                        animation: count > 0 ? "glow-pulse 2.2s ease-in-out infinite" : "none",
                    }}
                >
                    {muted
                        ? <BellOff size={20} color={count > 0 ? "#d97706" : "#94a3b8"} />
                        : <Bell
                            size={20}
                            color={count > 0 ? "#d97706" : "#94a3b8"}
                            style={count > 0 ? { animation: "bell-shake 2.8s ease-in-out infinite" } : {}}
                        />
                    }
                </button>

                {/* Badge */}
                {count > 0 && (
                    <div style={{
                        position: "absolute", top: -7, right: -7,
                        background: "#e65d5d", color: "#fff",
                        fontSize: "0.62rem", fontWeight: 800,
                        minWidth: 20, height: 20, borderRadius: 10,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        padding: "0 5px", border: "2.5px solid #fff",
                        animation: "badge-pop 0.35s cubic-bezier(.34,1.56,.64,1) forwards",
                        fontFamily: "'Inter', sans-serif",
                    }}>
                        {count > 99 ? "99+" : count}
                    </div>
                )}

                {/* ── Dropdown panel ── */}
                {open && (
                    <div
                        ref={panelRef}
                        style={{
                            position: "absolute",
                            top: "calc(100% + 12px)",
                            right: 0,
                            width: 360,
                            background: "#ffffff",
                            borderRadius: 16,
                            boxShadow: "0 14px 44px rgba(0,0,0,0.13), 0 3px 10px rgba(0,0,0,0.07)",
                            border: "1px solid #e2e8f0",
                            overflow: "hidden",
                            zIndex: 1000,
                            fontFamily: "'Inter', sans-serif",
                            animation: "panel-slide 0.22s cubic-bezier(.22,1,.36,1) forwards",
                        }}
                    >
                        {/* Header */}
                        <div style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            padding: "14px 16px",
                            borderBottom: "1px solid #f1f5f9",
                            background: "linear-gradient(135deg, #fffbeb 0%, #fef9ee 100%)",
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                                <div style={{
                                    width: 31, height: 31, borderRadius: 9,
                                    background: "linear-gradient(135deg, #fde68a, #fbbf24)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    boxShadow: "0 2px 6px rgba(251,191,36,0.4)",
                                }}>
                                    <Bell size={15} color="#78350f" />
                                </div>
                                <span style={{ fontSize: "0.88rem", fontWeight: 700, color: "#1e293b" }}>
                                    Notifications
                                </span>
                                {count > 0 && (
                                    <span style={{
                                        background: "#fbbf24", color: "#78350f",
                                        fontSize: "0.67rem", fontWeight: 800,
                                        padding: "2px 8px", borderRadius: 10,
                                    }}>
                                        {count}
                                    </span>
                                )}
                            </div>

                            {/* Mute/Unmute */}
                            <button
                                onClick={onToggleMute}
                                style={{
                                    display: "flex", alignItems: "center", gap: 5,
                                    padding: "6px 11px", borderRadius: 8, cursor: "pointer",
                                    border: `1.5px solid ${muted ? "#fca5a5" : "#bbf7d0"}`,
                                    background: muted ? "#fff5f5" : "#f0fdf4",
                                    color: muted ? "#e65d5d" : "#10b981",
                                    fontSize: "0.75rem", fontWeight: 700,
                                    transition: "opacity 0.2s", outline: "none",
                                    fontFamily: "'Inter', sans-serif",
                                }}
                                onMouseEnter={e => e.currentTarget.style.opacity = "0.75"}
                                onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                            >
                                {muted
                                    ? <><VolumeX size={13} /> Unmute</>
                                    : <><Volume2 size={13} /> Mute</>
                                }
                            </button>
                        </div>

                        {/* List */}
                        <div style={{
                            maxHeight: 420,
                            overflowY: "auto",
                            padding: count === 0 ? 0 : "12px 12px 4px 12px",
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                        }}>
                            {count === 0 ? (
                                <div style={{ padding: "40px 20px", textAlign: "center" }}>
                                    <div style={{
                                        width: 56, height: 56, borderRadius: 16,
                                        background: "#fef9c3",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        margin: "0 auto 12px",
                                    }}>
                                        <Bell size={26} color="#fbbf24" />
                                    </div>
                                    <div style={{ fontSize: "0.86rem", fontWeight: 700, color: "#475569" }}>All caught up!</div>
                                    <div style={{ fontSize: "0.74rem", marginTop: 5, color: "#94a3b8" }}>No pending notifications</div>
                                </div>
                            ) : (
                                updatedOrders.map(order => (
                                    <ToastItem
                                        key={order.id}
                                        order={order}
                                        onDismiss={onDismissOrder}
                                        onNavigate={(id) => window.open(`/order/${id}`, "_blank")}
                                    />
                                ))
                            )}
                        </div>

                        {/* Footer */}
                        {count > 0 && (
                            <div style={{
                                borderTop: "1px solid #f1f5f9",
                                padding: "10px 12px",
                                background: "#fafafa",
                            }}>
                                <button
                                    onClick={() => updatedOrders.forEach(o => onDismissOrder(o.id))}
                                    style={{
                                        width: "100%", padding: "8px", borderRadius: 9,
                                        background: "transparent",
                                        border: "1.5px solid #e2e8f0",
                                        color: "#64748b", fontSize: "0.78rem", fontWeight: 600,
                                        cursor: "pointer", transition: "all 0.2s",
                                        fontFamily: "'Inter', sans-serif", outline: "none",
                                    }}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.background = "#f1f5f9";
                                        e.currentTarget.style.color = "#1e293b";
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.background = "transparent";
                                        e.currentTarget.style.color = "#64748b";
                                    }}
                                >
                                    Dismiss all
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}